class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = new Map();
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.searchTerm = '';
    this.isOpen = false;
    this.requestId = 0;
    this.instanceId = `cf-search-${++PredictiveSearch.instanceCount}`;
    this.abortController = new AbortController();
    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    // Cancel immediately, including during SearchForm's existing debounce.
    this.input.addEventListener('input', () => {
      this.abortController.abort();
      this.requestId++;
      this.removeAttribute('results');
      this.closeResults();
    });
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() { return this.input.value.trim(); }

  onChange() {
    super.onChange();
    this.searchTerm = this.getQuery();
    if (this.searchTerm.length < 2) {
      this.close();
      return;
    }
    if (this.contains(document.activeElement)) this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery()) { event.preventDefault(); return; }
    // Also request pages and articles when Enter is pressed.
    let typeInput = this.input.form.querySelector('input[name="type"]');
    if (!typeInput) {
      typeInput = document.createElement('input');
      typeInput.type = 'hidden';
      typeInput.name = 'type';
      this.input.form.append(typeInput);
    }
    typeInput.value = 'product,page,article';
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.close(true);
    }
  }

  onFocus() {
    const query = this.getQuery();
    if (query.length < 2) return;
    if (query === this.searchTerm && this.getAttribute('results') === 'true') this.open();
    else {
      this.searchTerm = query;
      this.getSearchResults(query);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    }, 0);
  }

  onKeydown(event) {
    if (event.isComposing) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
    } else if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
      event.preventDefault();
      this.switchOption(event.key === 'ArrowUp' ? 'up' : 'down');
    } else if (event.key === 'Enter' && this.isOpen) {
      const selected = this.querySelector('[aria-selected="true"]');
      const action = selected?.matches('button') ? selected : selected?.querySelector('a, button');
      if (action) { event.preventDefault(); action.click(); }
    }
  }

  switchOption(direction) {
    if (!this.isOpen) return;
    const options = Array.from(this.predictiveSearchResults.querySelectorAll('[role="option"]'))
      .filter(element => element.getClientRects().length > 0);
    if (!options.length) return;
    const previous = this.querySelector('[aria-selected="true"]');
    const index = options.indexOf(previous);
    const next = index < 0 ? (direction === 'up' ? options.length - 1 : 0)
      : (index + (direction === 'up' ? -1 : 1) + options.length) % options.length;
    previous?.setAttribute('aria-selected', 'false');
    options[next].setAttribute('aria-selected', 'true');
    this.input.setAttribute('aria-activedescendant', options[next].id);
    options[next].scrollIntoView({ block: 'nearest' });
  }

  async fetchSection(url, signal, id) {
    const response = await fetch(url, { signal, headers: { Accept: 'text/html' } });
    if (!response.ok) throw new Error(`Search HTTP ${response.status}`);
    const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
    const section = doc.getElementById(`shopify-section-${id}`);
    if (!section?.querySelector('[data-cf-results]')) throw new Error(`Missing section: ${id}`);
    return section;
  }

  async getSearchResults(searchTerm) {
    this.abortController.abort();
    this.abortController = new AbortController();
    const signal = this.abortController.signal;
    const requestId = ++this.requestId;
    const key = JSON.stringify([window.Shopify?.routes?.root || '/', searchTerm.toLowerCase()]);
    if (this.cachedResults.has(key)) {
      this.renderSearchResults(this.cachedResults.get(key));
      return;
    }
    this.setLiveRegionLoadingState();
    try {
      const url = new URL(routes.predictive_search_url, window.location.origin);
      url.search = new URLSearchParams({
        q: searchTerm,
        'resources[type]': 'product,collection,query,page,article',
        'resources[limit]': '4',
        'resources[limit_scope]': 'each',
        'resources[options][fields]': 'title,body,vendor,variants.sku,product_type,variants.title',
        'resources[options][unavailable_products]': 'last',
        section_id: 'predictive-search'
      }).toString();
      const section = await this.fetchSection(url, signal, 'predictive-search');

      // Independent ordinary-search requests prevent products from crowding out pages/articles.
      // Only request a type when predictive search returned none of that type.
      const missing = ['product', 'page', 'article'].filter(type =>
        !section.querySelector(`[data-cf-resource="${type}s"]`));
      const fallbacks = await Promise.allSettled(missing.map(type => {
        const fallbackUrl = new URL(routes.search_url || `${window.Shopify?.routes?.root || '/'}search`, window.location.origin);
        fallbackUrl.search = new URLSearchParams({
          q: searchTerm, type,
          'options[prefix]': 'last',
          'options[unavailable_products]': 'last',
          section_id: 'cf-search-fallback'
        }).toString();
        return this.fetchSection(fallbackUrl, signal, 'cf-search-fallback');
      }));
      if (signal.aborted || requestId !== this.requestId || this.getQuery() !== searchTerm) return;
      let incomplete = false;
      fallbacks.forEach(result => {
        if (result.status === 'fulfilled') this.mergeGroups(section, result.value);
        else { incomplete = true; console.warn('Search fallback unavailable.', result.reason); }
      });
      this.finalizeMarkup(section, incomplete);
      const markup = section.innerHTML;
      if (!incomplete) {
        if (this.cachedResults.size >= 30) this.cachedResults.delete(this.cachedResults.keys().next().value);
        this.cachedResults.set(key, markup);
      }
      this.renderSearchResults(markup);
    } catch (error) {
      if (signal.aborted || requestId !== this.requestId || error.name === 'AbortError') return;
      this.close();
      this.setLiveRegionText(this.isSpanish() ? 'No se pudieron cargar las sugerencias. Presiona Enter para buscar.' : 'Suggestions could not load. Press Enter to search.');
      console.warn('Predictive search unavailable.', error);
    }
  }

  mergeGroups(target, source) {
    source.querySelectorAll('[data-cf-group]').forEach(sourceGroup => {
      const targetGroup = target.querySelector(`[data-cf-group="${sourceGroup.dataset.cfGroup}"]`);
      const list = targetGroup?.querySelector('ul');
      if (!list) return;
      const existing = new Set(Array.from(list.querySelectorAll('a')).map(link => link.getAttribute('href')));
      sourceGroup.querySelectorAll('li[data-cf-resource]').forEach(item => {
        const href = item.querySelector('a')?.getAttribute('href');
        if (!href || existing.has(href) || list.children.length >= 4) return;
        existing.add(href);
        list.append(item.cloneNode(true));
      });
    });
  }

  finalizeMarkup(section, incomplete) {
    section.querySelectorAll('[data-cf-group]').forEach(group => {
      if (!group.querySelector('[data-cf-resource]')) group.remove();
      else group.removeAttribute('hidden');
    });
    section.querySelectorAll('[data-cf-column]').forEach(column => {
      column.hidden = !column.querySelector('[data-cf-group]');
    });
    const count = Array.from(section.querySelectorAll('[data-cf-resource]'))
      .filter(item => !item.closest('[data-cf-group$="-mobile"]')).length;
    const wrapper = section.querySelector('#predictive-search-results-groups-wrapper');
    wrapper.hidden = count === 0;
    wrapper.classList.toggle('predictive-search__results-groups-wrapper--no-products', !section.querySelector('[data-cf-resource="products"]'));
    wrapper.classList.toggle('predictive-search__results-groups-wrapper--no-suggestions', !section.querySelector('[data-cf-resource="queries"], [data-cf-resource="collections"]'));
    const empty = section.querySelector('[data-cf-empty]');
    if (count > 0) empty.remove();
    else {
      empty.hidden = false;
      if (incomplete) empty.textContent = this.isSpanish()
        ? 'No se pudieron cargar todas las sugerencias. Presiona Enter para buscar.'
        : 'Some suggestions could not load. Press Enter to search.';
    }
    section.querySelector('[data-predictive-search-live-region-count-value]').textContent = this.isSpanish()
      ? `${count} sugerencias. Usa las flechas para navegar o Enter para buscar.`
      : `${count} suggestions. Use arrow keys to navigate or Enter to search.`;
  }

  isSpanish() { return (document.documentElement.lang || '').toLowerCase().startsWith('es'); }

  setLiveRegionLoadingState() {
    this.setAttribute('loading', 'true');
    this.setLiveRegionText(this.getAttribute('data-loading-text') || (this.isSpanish() ? 'Cargando…' : 'Loading…'));
  }

  setLiveRegionText(text) {
    const status = this.querySelector('.predictive-search-status');
    if (!status) return;
    status.setAttribute('aria-hidden', 'false');
    status.textContent = text;
    clearTimeout(this.statusTimeout);
    this.statusTimeout = setTimeout(() => status.setAttribute('aria-hidden', 'true'), 1000);
  }

  renderSearchResults(markup) {
    this.predictiveSearchResults.innerHTML = markup;
    // Prevent duplicate IDs across desktop/mobile search instances.
    const ids = new Map();
    this.predictiveSearchResults.querySelectorAll('[role="option"][id], h2[id]').forEach(element => {
      const old = element.id;
      element.id = `${this.instanceId}-${old}`;
      ids.set(old, element.id);
    });
    this.predictiveSearchResults.querySelectorAll('[aria-labelledby]').forEach(element => {
      element.setAttribute('aria-labelledby', element.getAttribute('aria-labelledby').split(' ').map(id => ids.get(id) || id).join(' '));
    });
    const listbox = this.predictiveSearchResults.querySelector('[role="listbox"]');
    if (listbox) this.input.setAttribute('aria-controls', listbox.id);
    this.input.removeAttribute('aria-activedescendant');
    this.setAttribute('results', 'true');
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]')?.textContent || '');
    if (this.contains(document.activeElement)) this.open();
  }

  open() {
    this.setAttribute('open', 'true');
    this.input.setAttribute('aria-expanded', 'true');
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.abortController.abort();
    this.requestId++;
    this.closeResults(clearSearchTerm);
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.searchTerm = '';
      this.removeAttribute('results');
    }
    this.querySelector('[aria-selected="true"]')?.setAttribute('aria-selected', 'false');
    this.input.removeAttribute('aria-activedescendant');
    this.input.setAttribute('aria-expanded', 'false');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.isOpen = false;
  }
}
PredictiveSearch.instanceCount = 0;
if (!customElements.get('predictive-search')) customElements.define('predictive-search', PredictiveSearch);